import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import VerifiedDoctorBadge from "@/components/common/VerifiedDoctorBadge";
import DoctorPhotoTile from "@/components/common/DoctorPhotoTile";
import { toast } from "sonner";
import { MapPin, Star, Clock, CheckCircle, ArrowLeft, Buildings, Translate, SpinnerGap } from "@phosphor-icons/react";
import { format } from "date-fns";
import { portalUrl } from "@/lib/host-mode";

/* ─────────────────────────────────────────────────────────────────────
 * Public booking page — `/book/:doctorId`
 *
 * Robust 4-stage flow (iter98.37 rewrite, addressing user bug report
 * 2026-06-12):
 *
 *   ① Anonymous user picks date + time + notes + REQUIRED NAME (and
 *      optional email). All validated client-side before any redirect
 *      so we never lose the user to OTP with an empty draft.
 *   ② On Confirm, the full draft is persisted in TWO places so the
 *      session survives the OTP hop:
 *        (a) sessionStorage["invisique_pending_booking"] — same-host
 *            preview / dev / "both" mode.
 *        (b) `b_*` query params on the patient-login URL — cross-host
 *            production (marketing site → portal subdomain) where
 *            sessionStorage doesn't cross hostnames.
 *      30-minute TTL via `ts` so stale drafts don't auto-fire later.
 *   ③ OTP / login completes inside Login.jsx. If `intent=book` is
 *      present in the URL params, Login.jsx finishes the booking
 *      inline (PUT /auth/profile if name needed + POST /appointments)
 *      and lands the user on /patient/appointments. No round-trip
 *      back to /book required (portal host doesn't have that route).
 *   ④ For SAME-HOST preview / dev flows the user does come back to
 *      /book/:doctorId after login. We detect the sessionStorage
 *      draft here, show a "Resuming your booking…" splash, and
 *      auto-submit on mount. Failures clear the draft and fall back
 *      to the manual form (with date/time/notes pre-filled).
 *
 * BUG FIXES THIS REPLACES:
 *   • Notes lost on login redirect (no persistence).
 *   • Date/time lost on login redirect (no persistence).
 *   • Brand-new patients created with name="" so doctor saw a phone-
 *     only "blank" appointment row.
 *   • Empty draft submitted because user clicked Confirm with no slot
 *     selected before being redirected to login.
 * ───────────────────────────────────────────────────────────────────── */

const BOOKING_DRAFT_KEY = "invisique_pending_booking";
const BOOKING_DRAFT_TTL_MS = 30 * 60 * 1000;  // 30 min

function readDraft(doctorId) {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.sessionStorage.getItem(BOOKING_DRAFT_KEY);
        if (!raw) return null;
        const draft = JSON.parse(raw);
        if (!draft || draft.doctorId !== doctorId) return null;
        if (!draft.ts || Date.now() - draft.ts > BOOKING_DRAFT_TTL_MS) {
            window.sessionStorage.removeItem(BOOKING_DRAFT_KEY);
            return null;
        }
        if (!draft.date || !draft.time) return null;
        return draft;
    } catch { return null; }
}

function clearDraft() {
    try { window.sessionStorage.removeItem(BOOKING_DRAFT_KEY); } catch { /* noop */ }
}

export default function BookAppointment() {
    const { doctorId } = useParams();
    const [searchParams] = useSearchParams();
    // iter100.6 — Doctor landing page hands off a pre-selected slot via
    // ?pre_date=YYYY-MM-DD&pre_time=HH:MM so the patient doesn't have to
    // re-pick after seeing availability on the previous screen. Both are
    // fully optional; absent → falls back to the default "tomorrow" seed.
    const preDate = searchParams.get("pre_date");
    const preTime = searchParams.get("pre_time");
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [doctor, setDoctor] = useState(null);
    const [date, setDate] = useState(() => {
        if (preDate) {
            const [y, m, d] = preDate.split("-").map(Number);
            const parsed = new Date(y, (m || 1) - 1, d || 1);
            if (!Number.isNaN(parsed.getTime())) return parsed;
        }
        return new Date(Date.now() + 24 * 60 * 60 * 1000);
    });
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(preTime || null);
    const [notes, setNotes] = useState("");
    /* Anonymous-only fields. Pre-fills from the saved draft on resume so
       returning users don't re-type. For authed users with a name on
       file, we skip the inputs entirely below. */
    const [guestName, setGuestName] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [booking, setBooking] = useState(false);
    /* Resume mode: when we've detected a pending draft for an authed
       user we suppress the manual form and show a single spinner
       while we silently fire the booking. */
    const [resuming, setResuming] = useState(false);
    const resumedRef = useRef(false);  // guard against double-fire under StrictMode

    useEffect(() => {
        api.get(`/doctors/${doctorId}`).then((r) => setDoctor(r.data)).catch(() => toast.error("Doctor not found"));
    }, [doctorId]);

    useEffect(() => {
        if (!date || !doctorId) return;
        const dateStr = format(date, "yyyy-MM-dd");
        api.get(`/doctors/${doctorId}/slots`, { params: { date: dateStr } }).then((r) => {
            setSlots(r.data);
            // iter100.6 — Preserve a landing-page pre-selection when the
            // fetched slots include it (and it's still available); otherwise clear.
            setSelectedSlot((prev) => {
                if (!prev) return null;
                const match = Array.isArray(r.data) && r.data.find((s) => s?.time === prev);
                return match && match.available !== false ? prev : null;
            });
        });
    }, [date, doctorId]);

    /* On mount: hydrate from any persisted draft for this doctor. Two
       behaviours depending on auth state:
         • Authed user + draft present → auto-resume (no manual form).
         • Anonymous user + draft present → pre-fill the form so users
           can review their selections before re-clicking Confirm.
       Anonymous + no draft = blank slate, normal first-visit UX. */
    useEffect(() => {
        const draft = readDraft(doctorId);
        if (!draft) return;
        setNotes(draft.notes || "");
        if (draft.name) setGuestName(draft.name);
        if (draft.email) setGuestEmail(draft.email);
        try {
            const d = new Date(`${draft.date}T${draft.time || "00:00"}:00`);
            if (!Number.isNaN(d.getTime())) setDate(d);
        } catch { /* invalid date in draft → skip pre-fill */ }
        if (user && !resumedRef.current) {
            resumedRef.current = true;
            setResuming(true);
        }
    }, [doctorId, user]);

    /* Pre-load slots for the draft's date THEN pick the saved time so
       selectedSlot is in-sync with the slots list. Otherwise the slot
       button can't visually highlight (slots haven't fetched yet) and
       the API call below would race against the slots GET. */
    useEffect(() => {
        if (!resuming) return;
        const draft = readDraft(doctorId);
        if (!draft) { setResuming(false); return; }
        // Best-effort restore visual state; slots will load via the date
        // effect above. We set selectedSlot directly from the draft.
        setSelectedSlot(draft.time);
    }, [resuming, doctorId]);

    /* Auto-resume the booking once we have an authed user and a draft.
       This effect runs after the draft + slots are in state. */
    const finishBooking = useCallback(async (draftOrNull) => {
        const draft = draftOrNull || readDraft(doctorId);
        const haveSlot = (draft && draft.time) || selectedSlot;
        if (!haveSlot) {
            toast.error("Please select a time slot");
            setResuming(false);
            return;
        }
        setBooking(true);
        try {
            // 1. If the user signed in fresh and has no name on file but
            //    we captured one during the public booking step, set it
            //    now so the doctor's agenda doesn't show a phone-only
            //    "blank" patient row.
            const captured = (draft && draft.name) || guestName;
            if (user && !user.name && captured) {
                try {
                    const r = await api.put("/auth/profile", { name: captured.trim() });
                    if (r.data && setUser) setUser(r.data);
                } catch {
                    // Non-fatal — appointment still proceeds. We toast a
                    // soft hint so the user knows to fix it later.
                    toast("Could not save your name — you can update it later in your profile.", { duration: 4000 });
                }
            }
            // 2. Fire the appointment.
            const apptDate = (draft && draft.date) || format(date, "yyyy-MM-dd");
            const apptTime = (draft && draft.time) || selectedSlot;
            const apptNotes = (draft && draft.notes) ?? notes;
            await api.post("/appointments", {
                doctor_id: doctorId,
                date: apptDate,
                time: apptTime,
                type: "consultation",
                notes: apptNotes,
            });
            clearDraft();
            toast.success("Appointment confirmed!");
            const dest = portalUrl("/patient/appointments");
            if (dest.startsWith("http")) {
                window.location.assign(dest);
            } else {
                navigate(dest);
            }
        } catch (err) {
            // Booking failed (e.g. slot already taken). Clear the draft
            // so the next page-load doesn't retry the doomed POST. The
            // user can re-select another slot manually.
            clearDraft();
            setResuming(false);
            toast.error(err.response?.data?.detail || "Booking failed");
        } finally {
            setBooking(false);
        }
    }, [doctorId, user, setUser, guestName, date, selectedSlot, notes, navigate]);

    useEffect(() => {
        if (!resuming || !user) return;
        // Wait one tick so date+slots state has settled before firing.
        const t = setTimeout(() => { finishBooking(); }, 250);
        return () => clearTimeout(t);
    }, [resuming, user, finishBooking]);

    /* "Confirm booking" click. Two paths:
         • Authed user: validate + POST inline.
         • Anonymous user: validate, persist draft, redirect to OTP
           login with `b_*` URL params so cross-host flows can finish
           the booking inline on the portal side. */
    const book = async () => {
        if (!selectedSlot) {
            toast.error("Please select a time slot");
            return;
        }
        const dateStr = format(date, "yyyy-MM-dd");
        if (!user) {
            const trimmedName = guestName.trim();
            if (!trimmedName) {
                toast.error("Please enter your name so the doctor knows who's coming.");
                return;
            }
            if (trimmedName.length < 2) {
                toast.error("Name looks too short — please use your full name.");
                return;
            }
            // Persist draft locally (same-host) AND on the URL
            // (cross-host). 30-min TTL bakes in via `ts`.
            const draft = {
                doctorId,
                date: dateStr,
                time: selectedSlot,
                notes: notes.trim(),
                name: trimmedName,
                email: guestEmail.trim(),
                ts: Date.now(),
            };
            try {
                window.sessionStorage.setItem(BOOKING_DRAFT_KEY, JSON.stringify(draft));
            } catch { /* private mode — URL params still carry it across */ }

            const qp = new URLSearchParams({
                intent: "book",
                b_doctor: doctorId,
                b_date: dateStr,
                b_time: selectedSlot,
                b_name: trimmedName,
                b_phone: "",  // unknown until OTP step; Login.jsx asks for it
            });
            if (notes.trim()) qp.set("b_notes", notes.trim());
            if (guestEmail.trim()) qp.set("b_email", guestEmail.trim());
            const loginPath = `/patient-login?${qp.toString()}`;
            const dest = portalUrl(loginPath);
            if (dest.startsWith("http")) {
                window.location.assign(dest);
            } else {
                // Same-host: also pass `from` so Login.jsx returns the
                // user to this page if the inline-finish path fails for
                // some reason (defence in depth).
                navigate(dest, { state: { from: `/book/${doctorId}` } });
            }
            return;
        }
        // Authed user — straight POST.
        await finishBooking({
            doctorId, date: dateStr, time: selectedSlot, notes: notes.trim(),
            name: user.name, email: user.email, ts: Date.now(),
        });
    };

    if (!doctor) {
        return <div className="max-w-7xl mx-auto px-5 py-20 text-center text-muted-foreground">Loading...</div>;
    }

    if (resuming) {
        return (
            <div className="max-w-md mx-auto px-5 py-24 text-center" data-testid="booking-resume-splash">
                <SpinnerGap size={32} className="mx-auto mb-4 animate-spin text-primary" />
                <h1 className="font-display text-2xl font-bold mb-2">Finishing your booking…</h1>
                <p className="text-muted-foreground text-sm">
                    We're confirming your {format(date, "EEEE, d MMM")} appointment with {doctor.name}.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="book-appointment-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
                <ArrowLeft size={14} /> Back
            </button>

            <div className="grid lg:grid-cols-12 gap-8">
                {/* Doctor card */}
                <div className="lg:col-span-4">
                    <div className="rounded-2xl overflow-hidden bg-card bento-shadow sticky top-24">
                        <DoctorPhotoTile
                            src={doctor.image}
                            name={doctor.name}
                            className="w-full"
                            testid="book-appointment-doctor-photo"
                        />
                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <h2 className="font-display text-2xl font-bold">{doctor.name}</h2>
                                <VerifiedDoctorBadge doctor={doctor} />
                            </div>
                            <p className="text-primary font-medium text-sm mb-4">{doctor.specialization}</p>
                            <div className="space-y-2 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2"><Star size={15} weight="fill" className="text-yellow-500" /> {doctor.rating} rating · {doctor.years_experience}+ yrs</div>
                                <div className="flex items-center gap-2"><Buildings size={15} /> {doctor.clinic_name}</div>
                                <div className="flex items-start gap-2"><MapPin size={15} className="mt-0.5" /> {doctor.address}, {doctor.city}</div>
                                <div className="flex items-center gap-2"><Translate size={15} /> {doctor.languages?.join(", ")}</div>
                            </div>
                            <div className="mt-5 pt-5 border-t border-border">
                                <div className="text-xs text-muted-foreground">Consultation fee</div>
                                <div className="font-display text-2xl font-bold">₹{doctor.consultation_fee}</div>
                            </div>
                            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{doctor.bio}</p>
                        </div>
                    </div>
                </div>

                {/* Booking */}
                <div className="lg:col-span-8 space-y-8">
                    <div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold">Book your consultation</h1>
                        <p className="text-muted-foreground mt-2">Pick a date and time that works for you.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-card rounded-2xl p-4 border border-border">
                            <Label className="overline text-primary mb-3 block">1. Select date</Label>
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={(d) => d && setDate(d)}
                                disabled={(d) => d < new Date(Date.now() - 86400000) || d > new Date(Date.now() + 60 * 86400000)}
                                className="rounded-md"
                                data-testid="date-calendar"
                            />
                        </div>
                        <div>
                            <Label className="overline text-primary mb-3 block">2. Select time</Label>
                            <div className="grid grid-cols-3 gap-2" data-testid="slots-grid">
                                {slots.map((s) => {
                                    /* iter98.54 — Distinguish "already booked" from
                                       "in the past" so users understand WHY a slot
                                       is greyed out. Both stay disabled (backend
                                       returns available:false for both), only the
                                       visual hint differs. */
                                    const isPast = !s.available && s.reason === "past";
                                    return (
                                    <button
                                        key={s.time}
                                        disabled={!s.available}
                                        onClick={() => setSelectedSlot(s.time)}
                                        className={`py-2.5 rounded-xl text-sm border transition-all ${
                                            selectedSlot === s.time
                                                ? "bg-primary text-white border-primary"
                                                : s.available
                                                ? "bg-card border-border hover:border-primary"
                                                : isPast
                                                ? "bg-muted/20 text-muted-foreground/60 border-dashed border-border cursor-not-allowed"
                                                : "bg-muted/40 text-muted-foreground border-border cursor-not-allowed line-through"
                                        }`}
                                        data-testid={`slot-${s.time}`}
                                        title={isPast ? "This time has already passed" : !s.available ? "Already booked" : ""}
                                    >
                                        {s.time}
                                    </button>
                                    );
                                })}
                            </div>
                            <div className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                                <Clock size={12} /> Duration: 30 min
                            </div>
                        </div>
                    </div>

                    {/* iter98.37 — Capture name + email for anonymous bookers
                        BEFORE the OTP redirect. Without this the appointment
                        landed in the doctor's agenda as a phone-only "blank"
                        row, which the doctor flagged on 2026-06-12. Authed
                        users skip this section since their profile already
                        has the name. */}
                    {!user && (
                        <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="guest-identity-block">
                            <Label className="overline text-primary block">3. Your details</Label>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="guest-name" className="text-xs font-semibold mb-1.5 block">Full name <span className="text-rose-500">*</span></Label>
                                    <Input
                                        id="guest-name"
                                        value={guestName}
                                        onChange={(e) => setGuestName(e.target.value)}
                                        placeholder="e.g. Priya Sharma"
                                        autoComplete="name"
                                        data-testid="guest-name-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="guest-email" className="text-xs font-semibold mb-1.5 block">Email <span className="text-muted-foreground italic">· optional</span></Label>
                                    <Input
                                        id="guest-email"
                                        type="email"
                                        value={guestEmail}
                                        onChange={(e) => setGuestEmail(e.target.value)}
                                        placeholder="for booking confirmation"
                                        autoComplete="email"
                                        data-testid="guest-email-input"
                                    />
                                </div>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                                We'll send the OTP to your phone on the next screen. Your details stay safe with us — used only for this appointment.
                            </p>
                        </div>
                    )}

                    <div>
                        <Label className="overline text-primary mb-3 block">{user ? "3." : "4."} Notes for the doctor <span className="text-muted-foreground italic text-[10px] tracking-normal">· optional, shown on their agenda</span></Label>
                        <Textarea
                            placeholder="Anything you'd like the doctor to know..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="rounded-xl"
                            data-testid="notes-input"
                        />
                    </div>

                    <div className="bg-secondary/50 rounded-2xl p-5 flex items-center gap-3">
                        <CheckCircle size={24} weight="duotone" color="hsl(192 100% 31%)" />
                        <div className="flex-1 text-sm">
                            {selectedSlot ? (
                                <span>
                                    <strong>{format(date, "EEEE, d MMM")}</strong> at <strong>{selectedSlot}</strong> with <strong>{doctor.name}</strong>
                                </span>
                            ) : (
                                <span className="text-muted-foreground">Select a time to confirm</span>
                            )}
                        </div>
                        <Button onClick={book} disabled={!selectedSlot || booking} className="rounded-full h-11 px-6" data-testid="confirm-booking-btn">
                            {booking ? "Booking..." : "Confirm booking"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
